import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Upload, FileText, Plus, Minus, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { apiFormatError } from "@/lib/api";
import { PROJECT_TYPES } from "@/lib/catalog";
import { useQuote } from "@/context/QuoteContext";
import { QuoteSelectionSummary } from "@/components/QuoteSelectionSummary";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

const EMPTY = { name: "", email: "", phone: "", project_type: "", zip_code: "", project_address: "", project_state: "", notes: "" };
const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine",
  "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
  "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
  "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia",
  "Washington", "West Virginia", "Wisconsin", "Wyoming",
];
const MAX_ATTACHMENT_SIZE = 15 * 1024 * 1024;
const MAX_ATTACHMENTS = 10;

function summarize(item) {
  if (!item) return "";
  return [item.series, item.finish, item.color, item.width && item.height ? `${item.width} × ${item.height}` : null]
    .filter(Boolean)
    .join(" · ");
}

export default function QuoteModal({ open, onClose, draftItem, onSubmitted }) {
  const { cart, addToCart, removeFromCart, updateQuantity, clearCart, openCsvImport } = useQuote();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{id, filename, size, content_type}]
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY,
        notes: draftItem?.notes || "",
        name: draftItem?.name || "",
        email: draftItem?.email || "",
        phone: draftItem?.phone || "",
      });
      setAttachments([]);
    }
  }, [open, draftItem]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while modal is open — prevents iOS Safari from
  // rubber-banding the underlying page and stealing scroll from the modal.
  useBodyScrollLock(open);

  const promoteDraftToCart = () => {
    if (!draftItem) return;
    addToCart(draftItem);
  };

  // Effective items list = cart + (draft if not yet in cart)
  const items = draftItem ? [{ ...draftItem, _uid: "__draft" }, ...cart] : cart;

  // Downscale big camera photos on the client before uploading. Keeps aspect
  // ratio and stays at 2048px on the longest side — visually identical for
  // plan/spec review but 5–10× smaller for a phone photo.
  const compressImage = async (file) => {
    const isImage =
      file.type &&
      file.type.startsWith("image/") &&
      // Only recompress formats a browser can decode
      /(jpeg|jpg|png|webp)$/i.test(file.type);
    if (!isImage) return file;
    const MAX = 2048;
    try {
      const bitmap = await createImageBitmap(file);
      const { width, height } = bitmap;
      if (Math.max(width, height) <= MAX) return file; // already small enough
      const scale = MAX / Math.max(width, height);
      const w = Math.round(width * scale);
      const h = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(bitmap, 0, 0, w, h);
      const blob = await new Promise((res) =>
        canvas.toBlob(res, "image/jpeg", 0.86)
      );
      if (!blob || blob.size >= file.size) return file; // no gain
      const stem = file.name.replace(/\.[^.]+$/, "");
      return new File([blob], `${stem}.jpg`, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    } catch {
      return file; // fall back to the original if anything goes wrong
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      toast.error(`Up to ${MAX_ATTACHMENTS} files per quote.`);
      return;
    }
    setUploading(true);
    // Compress images first (in parallel), then upload everything in parallel.
    const prepared = await Promise.all(
      files.map(async (f) => {
        if (f.size > MAX_ATTACHMENT_SIZE) return { skip: f, reason: "size" };
        const out = await compressImage(f);
        return { file: out };
      })
    );
    const uploadJobs = prepared.map(async (p) => {
      if (p.skip) {
        toast.error(`${p.skip.name} exceeds 15MB`);
        return null;
      }
      try {
        const fd = new FormData();
        fd.append("file", p.file);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return data;
      } catch (e) {
        toast.error(
          apiFormatError(e.response?.data?.detail) || `Upload failed: ${p.file.name}`
        );
        return null;
      }
    });
    const results = await Promise.allSettled(uploadJobs);
    const successes = results
      .map((r) => (r.status === "fulfilled" ? r.value : null))
      .filter(Boolean);
    if (successes.length) setAttachments((a) => [...a, ...successes]);
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    // Strip local-only fields (_uid) before sending; also pull any reference
    // photos uploaded inside the "Design your own" modal into attachment_ids.
    const items = cart.map(({ _uid, ...rest }) => rest);
    const refAttachmentIds = cart
      .map((it) => it.reference_photo_id)
      .filter(Boolean);
    const allAttachmentIds = Array.from(
      new Set([...attachments.map((a) => a.id), ...refAttachmentIds])
    );
    if (items.length === 0 && allAttachmentIds.length === 0) {
      toast.error("Your cart is empty. Please configure at least one door.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/quotes", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        project_type: form.project_type || null,
        zip_code: form.zip_code || null,
        project_address: form.project_address || null,
        project_state: form.project_state || null,
        items,
        attachment_ids: allAttachmentIds,
        notes: form.notes || null,
      });
      const totalDoors = items.reduce((s, it) => s + Number(it.quantity || 1), 0);
      toast.success(
        `Quote received — ${totalDoors} door${totalDoors === 1 ? "" : "s"} configured. Our atelier will be in touch shortly.`
      );
      // Meta Pixel — track qualified Lead conversion (fires only after server confirms).
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }
      clearCart();
      setAttachments([]);
      onSubmitted?.();
      onClose();
    } catch (e2) {
      toast.error(apiFormatError(e2.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] bg-brand-charcoal/70 backdrop-blur"
            onClick={onClose}
            data-testid="quote-modal-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[90] flex items-start justify-center p-4 md:p-8 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
            onClick={(e) => {
              // Backdrop click on the scroll wrapper closes the modal
              if (e.target === e.currentTarget) onClose();
            }}
          >
            <div
              className="w-full max-w-4xl bg-brand-bone border border-brand-green/25 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="quote-modal"
            >
              <div className="relative bg-brand-green text-brand-bone p-6 md:p-8">
                <button
                  className="absolute top-4 right-4 text-brand-bone/70 hover:text-brand-gold"
                  onClick={onClose}
                  data-testid="quote-modal-close"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
                <span className="text-[0.7rem] tracking-[0.32em] uppercase text-brand-gold">
                  Request a Quote
                </span>
                <h3 className="mt-3 font-serif text-3xl md:text-4xl">
                  Tell us about your <span className="italic text-brand-gold">project.</span>
                </h3>
                <p className="mt-2 text-brand-bone/75 text-sm max-w-xl">
                  Upload your floor plans and door schedule — our atelier will price the entire
                  scope for you.
                </p>
              </div>

              <QuoteSelectionSummary items={cart} />

              {/* Attachments */}
              <div className="px-6 md:px-8 py-6 border-b border-brand-green/15">
                <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-ink/60 mb-3">
                  Door Schedule · Plans · References
                </div>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`cursor-pointer border-2 border-dashed p-6 flex flex-col items-center justify-center text-center transition-colors ${
                    dragOver ? "border-brand-gold bg-brand-gold/10" : "border-brand-green/30 hover:border-brand-green"
                  }`}
                  data-testid="quote-dropzone"
                >
                  <Upload size={22} className="text-brand-green mb-2" />
                  <div className="text-sm text-brand-ink">
                    <span className="font-medium">Drop files here</span> or click to browse
                  </div>
                  <div className="text-[0.62rem] tracking-[0.2em] uppercase text-brand-ink/50 mt-1">
                    PDF · DWG · PNG · JPG · XLSX · up to 15MB · max {MAX_ATTACHMENTS} files
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    accept=".pdf,.dwg,.dxf,.png,.jpg,.jpeg,.webp,.gif,.xlsx,.xls,.csv,.docx,.doc,.txt,image/*,application/pdf"
                    onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
                    data-testid="quote-file-input"
                  />
                </div>
                {uploading && (
                  <div className="mt-3 text-[0.62rem] tracking-[0.28em] uppercase text-brand-gold">Uploading…</div>
                )}
                {attachments.length > 0 && (
                  <ul className="mt-4 space-y-2" data-testid="quote-attachment-list">
                    {attachments.map((a) => (
                      <li key={a.id} className="flex items-center gap-3 bg-brand-bone-2 border border-brand-green/15 px-3 py-2">
                        <FileText size={16} className="text-brand-green shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-brand-ink truncate">{a.filename}</div>
                          <div className="text-[0.62rem] tracking-[0.14em] uppercase text-brand-ink/50">
                            {a.content_type} · {(a.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(a.id)}
                          className="text-brand-ink/50 hover:text-red-800"
                          data-testid={`quote-remove-attachment-${a.id}`}
                          aria-label="Remove attachment"
                        >
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <form onSubmit={submit} className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full Name" required>
                  <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sofia Marchetti" data-testid="quote-name" required />
                </Field>
                <Field label="Email" required>
                  <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="quote-email" required />
                </Field>
                <Field label="Phone">
                  <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 (000) 000-0000" data-testid="quote-phone" />
                </Field>
                <Field label="Zip / Postal">
                  <input className="input" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} placeholder="90210" data-testid="quote-zip" />
                </Field>
                <Field label="Project Address" full required>
                  <input
                    className="input"
                    value={form.project_address}
                    onChange={(e) => setForm({ ...form, project_address: e.target.value })}
                    placeholder="1234 Sunset Blvd, Los Angeles"
                    data-testid="quote-project-address"
                    required
                  />
                  <p className="text-[0.6rem] text-brand-ink/50 mt-1">
                    Where the doors will be installed — helps us route your quote to the right showroom
                  </p>
                </Field>
                <Field label="Project State" required>
                  <select
                    className="input"
                    value={form.project_state}
                    onChange={(e) => setForm({ ...form, project_state: e.target.value })}
                    data-testid="quote-project-state"
                    required
                  >
                    <option value="">Select a state</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <Field label="I am a…" full>
                  <div className="flex flex-wrap gap-2">
                    {PROJECT_TYPES.map((p) => (
                      <button
                        type="button"
                        key={p}
                        onClick={() => setForm({ ...form, project_type: p })}
                        className={`px-3 py-2 text-[0.62rem] tracking-[0.2em] uppercase border ${
                          form.project_type === p
                            ? "border-brand-green bg-brand-green text-brand-bone"
                            : "border-brand-ink/20 text-brand-ink/70 hover:border-brand-green"
                        }`}
                        data-testid={`quote-project-${p.replace(/\W+/g, "-").toLowerCase()}`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Project Notes" full>
                  <textarea className="input min-h-[92px] resize-y" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Tell us about the scope, timeline, install date, or reference details." data-testid="quote-notes" />
                </Field>

                <div className="md:col-span-2 flex items-center justify-between pt-2 border-t border-brand-green/15 mt-2">
                  <button type="button" onClick={onClose} className="text-[0.72rem] tracking-[0.28em] uppercase text-brand-ink/60 hover:text-brand-green" data-testid="quote-cancel">
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting || (cart.length === 0 && attachments.length === 0)} className={`btn-primary ${submitting || (cart.length === 0 && attachments.length === 0) ? "opacity-50 pointer-events-none" : ""}`} data-testid="quote-submit">
                    {submitting ? "Submitting…" : `Send Request · ${cart.reduce((s, it) => s + Number(it.quantity || 1), 0)} door${cart.reduce((s, it) => s + Number(it.quantity || 1), 0) === 1 ? "" : "s"} →`}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Field({ label, children, full, required }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-ink/60">
        {label} {required && <span className="text-brand-gold">*</span>}
      </span>
      {children}
      <style>{`
        .input { background: white; border: 1px solid rgba(23,21,18,0.18); padding: 12px 14px; color: var(--brand-ink); font-size: 14px; }
        .input:focus { outline: none; border-color: var(--brand-green); }
      `}</style>
    </label>
  );
}
